/**
 * Device Info Collection
 * Coleta informações do dispositivo (iOS/Android/Web)
 */

import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';

export interface DeviceInfo {
  platform: 'ios' | 'android' | 'web' | 'edge' | 'server';
  app_version: string;
  build_number: string;
  os_version?: string;
  device_model?: string;
}

let cachedDeviceInfo: DeviceInfo | null = null;

/**
 * Coleta informações do dispositivo
 */
export async function getDeviceInfo(): Promise<DeviceInfo> {
  // Retornar cache se disponível
  if (cachedDeviceInfo) {
    return cachedDeviceInfo;
  }

  let info: DeviceInfo;

  // Detectar plataforma
  if (typeof window === 'undefined') {
    // Server-side (Edge Functions)
    info = {
      platform: 'edge',
      app_version: '1.0.0',
      build_number: '1',
    };
  } else if (Capacitor.isNativePlatform()) {
    // Mobile (iOS/Android)
    try {
      const [deviceInfo, appInfo] = await Promise.all([
        Device.getInfo(),
        App.getInfo()
      ]);
      const platform = Capacitor.getPlatform() as 'ios' | 'android';
      
      info = {
        platform,
        app_version: appInfo.version || '1.0.0',
        build_number: appInfo.build || '1',
        os_version: `${deviceInfo.operatingSystem} ${deviceInfo.osVersion}`,
        device_model: deviceInfo.model,
      };
    } catch (error) {
      console.error('Failed to get device info:', error);
      info = {
        platform: 'ios',
        app_version: '1.0.0',
        build_number: '1',
      };
    }
  } else {
    // Web
    info = {
      platform: 'web',
      app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      build_number: import.meta.env.VITE_BUILD_NUMBER || '1',
      os_version: navigator.userAgent,
      device_model: getBrowserInfo(),
    };
  }

  // Cache para chamadas futuras
  cachedDeviceInfo = info;
  return info;
}

/**
 * Detecta informações do browser
 */
function getBrowserInfo(): string {
  const ua = navigator.userAgent;
  
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edge')) return 'Edge';
  
  return 'Unknown';
}

/**
 * Limpa cache (útil para testes)
 */
export function clearDeviceInfoCache() {
  cachedDeviceInfo = null;
}
